import { Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CodeMirrorLanguage } from 'apps/account/src/account-shared/code-mirror/code-mirror.util';
import { FtCodeMirrorComponent } from 'apps/account/src/account-shared/code-mirror/ft-code-mirror.component';
import { MODAL_EDITOR_TOKEN, ModalEditorBase } from '../../../classes/modal-editor-base';
import { sanitizeHtml } from '../../../utils/sanitize-html.util';

/**
 * The modal code editor behind every CSS / JSON panel in the app (touchpoint styling, domain URL
 * mappings, third-party integration configuration).
 *
 * Was Monaco (`<ngx-monaco-editor>` plus a 4.8 MB `assets/monaco-editor/min` asset copy); now
 * CodeMirror 6 via `ft-code-mirror`, which loads on demand. Selector, providers and the
 * `applyChanges()` html-sanitise behaviour are unchanged, so nothing downstream has to know.
 *
 * Monaco's `formatOnPaste` / `formatOnType` are gone — reformatting mid-keystroke has no
 * CodeMirror equivalent that is not a fight with the caret, and for CSS specifically Monaco had
 * already lost its formatting provider when `css-format-monaco` was removed (FTK-7503). The
 * document is still beautified when the dialog opens (Monaco ran `editor.action.formatDocument` on
 * init), and the Format button is the explicit replacement for the on-type/on-paste passes. Both
 * go through the same `window.css_beautify` / `window.js_beautify` the contest field editors use.
 *
 * `language` covers css and json — the only two any template passes (19 and 3 instances). The old
 * union also declared `typescript`, which nothing used and which could not have worked anyway:
 * `angular.json` copied Monaco's `vs/language/typescript/tsMode.js` but never its 4.6 MB
 * `tsWorker.js`, so selecting it would have 404'd rather than produced an editor.
 */
@Component({
    selector: 'ft-code-editor',
    templateUrl: './code-editor.component.html',
    styleUrls: ['./code-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => CodeEditorComponent)
        },
        {
            provide: MODAL_EDITOR_TOKEN,
            useExisting: CodeEditorComponent
        }
    ],
    standalone: false
})
export class CodeEditorComponent extends ModalEditorBase {
    @Input() language: CodeMirrorLanguage;
    @Input() height = 480;

    editor: FtCodeMirrorComponent;

    constructor() {
        super();
    }

    /**
     * Monaco ran `editor.action.formatDocument` behind a `setTimeout` when the dialog opened, and
     * the resulting model change flowed back through `ngModel` — so what got saved was the
     * formatted text. Keep both halves. The timeout is not cosmetic: formatting synchronously
     * inside the `(ready)` handler would write `value` inside a change-detection pass that already
     * read it.
     */
    onEditorReady(editor: FtCodeMirrorComponent) {
        this.editor = editor;
        setTimeout(() => editor.format());
    }

    /** Beautify the whole document (js-beautify, via the window globals). */
    formatContent() {
        this.editor?.format();
    }

    applyChanges() {
        if (this.language === 'html') {
            this.value = sanitizeHtml(this.value);
        }
        super.applyChanges();
    }
}
